import { useState, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { formatCivilDate } from '@eis/core';
import { EmptyState } from '@eis/patterns';
import type { InstanceApi } from '@eis/insuranceservice-sdk';
import { useGroupPolicyPartnerHistory, useGroupPolicyTimeline } from '../api/group-policies';
import { isSuperseded, visibleSegments } from '../model/types';
import { partnersDiffer } from '../../policies/model/partners';
import { PartnerHistoryPanel } from '../../policies/ui/PartnerHistoryPanel';

/**
 * Zeitverlauf — a read-side PROJECTION, never a stored object (decision S-6).
 *
 * It reads in the operations persona's words. There is no "version" on this screen: each row is a
 * period the agreement was believed to look a certain way, and the ones a correction replaced are
 * HIDDEN BY DEFAULT until somebody asks for them.
 *
 * Hidden, not deleted. Superseded beliefs are kept forever — GoBD and premium reproducibility need
 * every historical belief reconstructable — and the toggle is what makes that visible without
 * making every reader wade through it.
 */
export function GroupPolicyTimelineTab({
  code,
  rules,
  locale,
}: {
  readonly code: string;
  readonly rules?: readonly InstanceApi.PartnerRoleRule[];
  readonly locale: string;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const [showSuperseded, setShowSuperseded] = useState(false);
  const timeline = useGroupPolicyTimeline(code);
  const history = useGroupPolicyPartnerHistory(code);
  const segments = visibleSegments(timeline.data?.segments, showSuperseded);

  return (
    <Stack spacing={2}>
      <FormControlLabel
        control={
          <Switch
            checked={showSuperseded}
            onChange={(event) => {
              setShowSuperseded(event.target.checked);
            }}
          />
        }
        label={t('groupPolicies.timeline.showSuperseded')}
      />

      {timeline.isPending ? (
        <Skeleton variant="rounded" height={180} />
      ) : segments.length === 0 ? (
        <EmptyState description={t('groupPolicies.timeline.empty')} />
      ) : (
        <Paper variant="outlined" component="section" sx={{ p: 2.5 }}>
          <Box
            component="ol"
            aria-label={t('groupPolicies.timeline.title')}
            sx={{ listStyle: 'none', m: 0, p: 0 }}
          >
            {segments.map((segment, index) => {
              const snapshot = segment.snapshot;
              const superseded = isSuperseded(segment);
              return (
                <Box
                  key={`${snapshot?.validFrom ?? ''}-${snapshot?.recordedAt ?? String(index)}`}
                  component="li"
                  sx={{
                    pl: 3,
                    pb: index === segments.length - 1 ? 0 : 2,
                    borderLeft: index === segments.length - 1 ? 0 : 1,
                    borderColor: 'divider',
                    ml: 0.75,
                    // Legible but visibly not the current belief. Colour is never the only
                    // carrier: the chip says it in words as well.
                    opacity: superseded ? 0.6 : 1,
                  }}
                >
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Typography variant="subtitle2" component="span">
                      {t('groupPolicies.timeline.period', {
                        from:
                          snapshot?.validFrom === undefined || snapshot.validFrom === ''
                            ? '—'
                            : formatCivilDate(snapshot.validFrom, locale),
                        to:
                          snapshot?.validTo === undefined || snapshot.validTo === ''
                            ? t('groupPolicies.timeline.open')
                            : formatCivilDate(snapshot.validTo, locale),
                      })}
                    </Typography>
                    {superseded && (
                      <Chip size="small" label={t('groupPolicies.timeline.superseded')} />
                    )}
                    {index > 0 &&
                      partnersDiffer(
                        segments[index - 1]?.groupPolicy?.partners,
                        segment.groupPolicy?.partners,
                      ) && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={t('groupPolicies.timeline.partnersChanged')}
                          data-testid="partners-changed"
                        />
                      )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {t('groupPolicies.timeline.recordedAt', { at: snapshot?.recordedAt ?? '' })}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}

      <PartnerHistoryPanel history={history} rules={rules} locale={locale} />
    </Stack>
  );
}
